// One live connection to a server's room. Owns the socket, the in-memory message store, optimistic sends,
// reconnects that resume exactly where they left off, presence and typing. React reads it via useSyncExternalStore.
//
// The store is also kept on the device (cache.ts): a new Room draws the cached channels and messages at once, then
// tells the room its cursors and change counter so it fills exactly the gap. Every change to the store is written
// back a moment later.
import { ulid } from "ulid";
import {
  isReactionEffect,
  HISTORY_MAX,
  TYPING_THROTTLE_MS,
  TYPING_TTL_MS,
  type Attachment,
  type Category,
  type Channel,
  type ClientEvent,
  type CustomEmoji,
  customEmojiId,
  type Sticker,
  type MemberProfile,
  type Member,
  type Message,
  type Forward,
  type NotifyLevel,
  type MuteFor,
  type ServerNotify,
  muteUntil,
  type ServerEvent,
  type SearchHit,
  type SearchFilters,
  isThreadStream,
  BLOOM_ID,
  threadParentId,
  threadStreamId,
  type AppEvent,
  type AppManifest,
  type BotAccess,
  type BotInfo,
  type AppRecord,
  type Card,
  type PrivateCard,
  type AppVersion,
  type CardPreviews,
  type BloomingThread,
  type BloomWho,
  type ListInfo,
  type ListItem,
  type ListVisibility,
  PINS,
  type IntegrationSetting,
} from "@bloom/shared";
import { AppData, type AppDataSource } from "./apps/data.ts";
import { record } from "./metrics.ts";
import { ActiveReporter } from "./activeReport.ts";
import { isMobileForm } from "./push.ts";
import { track } from "./analytics.ts";
import { playJumboLanding, playReactionEffect } from "./reactionEffects.ts";
import { rememberAvatars } from "./people.ts";
import { countsAsMention, READ_DEBOUNCE_MS } from "./readState.ts";
import { CACHE_MESSAGES, loadServer, save, saveMembers, wipeServer, type CachedStream } from "./cache.ts";

export type Status = "connecting" | "live" | "offline";

export interface ChatMessage extends Message {
  /** pending: sent, not yet confirmed. failed: the room refused it or we gave up; tap to retry. */
  state?: "pending" | "failed";
  error?: string;
  /** A forward I sent from this device: the id of the message I forwarded (a retry sends it again). */
  forwardOf?: string;
}

export interface ChannelState {
  messages: ChatMessage[];
  /** Older messages exist above the first one we hold. */
  more: boolean;
  loadingOlder: boolean;
}

export interface Snapshot {
  status: Status;
  me: string | null;
  categories: Category[];
  channels: Channel[];
  byChannel: Record<string, ChannelState>;
  online: Set<string>;
  /** channelId → userId → expiry (ms). */
  typing: Record<string, Record<string, number>>;
  /** How far I've read each channel/DM (synced across my devices by the room). Missing: never read. */
  read: Record<string, number>;
  /** Unread messages that mention me (or any unread DM message), per channel/DM. */
  mentionCounts: Record<string, number>;
  /** My notification levels where I picked one (synced across my devices); missing: the server's. */
  notify: Record<string, NotifyLevel>;
  /** Channels and DMs I muted: until when (ms, or MUTE_FOREVER). May hold ended mutes: check with muteActive. */
  mutes: Record<string, number>;
  /** DMs I closed in the sidebar (synced across my devices): the seq each ended on then. See dmHidden. */
  dmClosed: Record<string, number>;
  /** My settings for this whole server (level, @everyone, server mute). */
  serverNotify: ServerNotify;
  /** Caught up with the room on this connection. False while drawing from the device cache or reconnecting. */
  synced: boolean;
  /** Apps installed in this server (from "ready"); the command registry is built from these. */
  apps: AppManifest[];
  /** My private cards ("Only you can see this"), oldest first. Never cached on the device. */
  privateCards: PrivateCard[];
  /** The server's blooming threads (apps being built by chatting), oldest first. */
  blooming: BloomingThread[];
  /** Who may start blooming here. */
  bloomWho: BloomWho;
  /** Which integrations are on in this server (no keys). */
  integrations: IntegrationSetting[];
  /** The server's bots (docs/bots.md): who they are, who may talk to them, where they are. */
  bots: BotInfo[];
  /** The server's custom emoji and stickers (from "ready", then "customEmoji" events). */
  emojis: CustomEmoji[];
  stickers: Sticker[];
  /** Lists I can see: pins of channels I can see, shared lists, my private lists (from "ready", kept live). */
  lists: ListInfo[];
  /** What's in my private lists: list id → message ids, newest added first. */
  privateItems: Record<string, string[]>;
  /**
   * The room's words for something it refused that names no message (a delete, an edit or a reaction it won't do),
   * to show once as a toast. `n` counts up, so the same words twice are two notices (#226).
   */
  notice: { n: number; text: string } | null;
}

/** The answer to an action call (or restore). */
export type AppResult = { ok: true; result: unknown } | { ok: false; message: string; errors?: Record<string, string> };

const emptyChannel = (): ChannelState => ({ messages: [], more: false, loadingOlder: false });

/** Fired on window when the room says this sign-in has ended; the session provider then rechecks who's signed in. */
export const SIGNED_OUT_EVENT = "bloom:signed-out";
/**
 * Opening a channel lands on its first unread message (the NEW line) when there are at most this many unread;
 * that many are loaded to get there. More than that, it opens at the bottom.
 */
export const MAX_UNREAD_JUMP = 1000;
/** How far back a jump to one message (search, a collection, a message link, a quote) loads to reach it. */
export const MAX_MESSAGE_JUMP = 50_000;
/** Unread channels fetched ahead of time (up to their first unread message), this many at once. */
const PREFETCH_CONCURRENCY = 2;
/** Changes are written to the device cache this long after the last one. */
const SAVE_DELAY_MS = 500;
/** A send with no ack after this long on a live socket is shown as failed (#514: the clock stops while the socket is down). */
const SEND_CLOCK_MS = 15_000;
/** A socket that has not opened by now is dropped and tried again; the heartbeat checks every 5 s, so it is 10 to 15 s. */
const CONNECT_TIMEOUT_MS = 10_000;
/** If IndexedDB hasn't answered by then, connect without the cache rather than wait. */
const CACHE_LOAD_TIMEOUT_MS = 1000;

/** A channel as the room sent it, noting the `lastSeq` its week's count goes with (`newSinceRead`, #59). */
const withWeekSeq = (c: Channel): Channel => (c.weekMessages === undefined ? c : { ...c, weekSeq: c.lastSeq });

export class Room {
  private ws: WebSocket | null = null;
  private listeners = new Set<() => void>();
  private snap: Snapshot = { status: "connecting", me: null, categories: [], channels: [], byChannel: {}, online: new Set(), typing: {}, read: {}, mentionCounts: {}, notify: {}, mutes: {}, dmClosed: {}, serverNotify: {}, synced: false, apps: [], privateCards: [], emojis: [], stickers: [], lists: [], privateItems: {}, blooming: [], bloomWho: "everyone", integrations: [], bots: [], notice: null };
  /** Live app records (see apps/data.ts) and how many views watch each "app/collection". */
  readonly appData = new AppData();
  private watching = new Map<string, number>();
  private sentAt = new Map<string, number>();
  /** The 15 s clock of each send (#514): it runs while the socket is live, and stops when the socket drops. */
  private sendClocks = new Map<string, number>();
  /** Counts what the socket sent us; a send clock compares it with the count at its start (#514). */
  private heardCount = 0;
  private outbox: Extract<ClientEvent, { t: "send" }>[] = [];
  private attempt = 0;
  /** When the current socket started to connect: one still connecting after CONNECT_TIMEOUT_MS is dropped (#514). */
  private connectStartedAt = 0;
  private closedAt: number | null = null;
  private stopped = false;
  private lastTypingSent = new Map<string, number>();
  private clockOffset = 0; // room time − local time, from pings
  private pingTimer: number | undefined;
  private typingTimer: number | undefined;
  private reconnectTimer: number | undefined;
  private rid = 0;
  private lastHeard = 0;
  private heartbeat: number | undefined;
  private lastBeat = 0;
  private pendingRequests = new Map<string, (e: ServerEvent) => void>();
  /** Read positions not yet sent to the room (debounced, and kept while offline). */
  private unsentReads = new Map<string, number>();
  private readTimer: number | undefined;
  /** Tells the room whether I'm in front of this page and when I last did something (it holds my phone's push meanwhile, #683). */
  private active = new ActiveReporter({
    mobile: isMobileForm(),
    front: () => document.visibilityState === "visible" && document.hasFocus(),
    send: (f) => this.raw(f),
  });
  /** Listeners for server-level events the snapshot doesn't hold (renames, roles, people leaving). */
  private serverListeners = new Set<(e: ServerLevelEvent) => void>();

  // ---- Device cache ----
  /** Resolves once the cache has been read (or found empty); hello waits for it so it can send the cached cursors. */
  readonly loaded: Promise<void>;
  /** The member list as last saved on this device (names for the first paint), once `loaded`. */
  cachedMembers: Member[] | undefined;
  /** The room's change counter that the messages we hold are up to date with (see hello's `rev`). */
  private rev: number | undefined;
  /** The counter from the latest "ready"; it becomes `rev` once that catch-up has fully arrived ("synced"). */
  private pendingRev: number | undefined;
  /** Conversations changed since the last save, conversations to delete, and whether the server record changed. */
  private dirty = new Set<string>();
  private drop = new Set<string>();
  private serverDirty = false;
  private saveTimer: number | undefined;
  /** False until the cache is read, so an empty start can't overwrite it; false again once this server is forgotten. */
  private persisting = false;
  /** Cached threads, drawn at once when one is opened. */
  private threadCache = new Map<string, CachedStream>();
  /** The channel on screen: caught up first, and prefetched first. */
  private focus: string | undefined;
  /** Loads back to a seq or message in progress, per stream. */
  private loadingBack = new Map<string, Promise<void>>();
  private coldLiveRecorded = false;

  constructor(
    readonly serverId: string,
    readonly userId: string,
  ) {
    this.loaded = this.loadCache();
    this.connect();
    addEventListener("online", this.wake);
    addEventListener("offline", this.wentOffline);
    document.addEventListener("visibilitychange", this.onVisible);
    addEventListener("focus", this.reportActive);
    addEventListener("blur", this.reportActive);
    for (const t of ACTIVITY) addEventListener(t, this.onInput, { passive: true, capture: true });
    addEventListener("pagehide", this.saveNow);
    this.heartbeat = window.setInterval(() => {
      this.checkAlive();
      this.reportActive(); // notices going idle
    }, 5_000);
  }

  // ---- React glue ----
  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getSnapshot = () => this.snap;
  /** The server was renamed or recoloured, someone's role changed, or someone left. */
  onServerEvent = (fn: (e: ServerLevelEvent) => void) => {
    this.serverListeners.add(fn);
    return () => void this.serverListeners.delete(fn);
  };

  /** Someone changed their name or avatar. Member lists live outside the room's snapshot, so pages listen here. */
  private memberListeners = new Set<(m: MemberProfile) => void>();
  onMemberUpdated = (fn: (m: MemberProfile) => void) => {
    this.memberListeners.add(fn);
    return () => void this.memberListeners.delete(fn);
  };
  private emit(patch: Partial<Snapshot>) {
    const before = this.snap;
    this.snap = { ...this.snap, ...patch };
    if (patch.status === "live" && this.liveWaiters.length) for (const w of this.liveWaiters.splice(0)) w();
    // Note what the device cache needs to hear about.
    if (patch.categories || patch.channels || patch.read || patch.mentionCounts || patch.notify || patch.mutes || patch.dmClosed || patch.serverNotify || patch.emojis || patch.stickers) this.serverDirty = true;
    if (patch.byChannel) for (const [id, ch] of Object.entries(patch.byChannel)) if (before.byChannel[id] !== ch) this.dirty.add(id);
    if (this.serverDirty || this.dirty.size) this.scheduleSave();
    for (const fn of this.listeners) fn();
  }

  // ---- Device cache ----

  /** Draws whatever this device kept for the server, unless the room has already answered. */
  private async loadCache() {
    const cached = await Promise.race([loadServer(this.userId, this.serverId), new Promise<null>((r) => setTimeout(() => r(null), CACHE_LOAD_TIMEOUT_MS))]);
    this.persisting = true;
    if (!cached) return;
    this.cachedMembers = cached.members;
    const server = cached.server;
    if (!server || this.snap.channels.length || this.stopped) return;
    const visible = new Set(server.channels.map((c) => c.id));
    const byChannel: Record<string, ChannelState> = {};
    for (const s of cached.streams) {
      if (isThreadStream(s.streamId)) this.threadCache.set(s.streamId, s);
      else if (visible.has(s.streamId)) {
        // The build before #234 could save `more: false` over messages that start above seq 1. Seqs start at 1 and the
        // room keeps deleted messages as tombstones, so a first held seq above 1 means older messages exist.
        byChannel[s.streamId] = { messages: s.messages, more: s.more || (s.messages[0]?.seq ?? 1) > 1, loadingOlder: false };
      }
    }
    // Empty channels can show as empty; a channel with messages we never kept waits for the room.
    for (const c of server.channels) if (c.lastSeq === 0) byChannel[c.id] ??= emptyChannel();
    // Resume from the oldest counter any of these messages is fresh as of (see CachedStream.rev).
    const revs = [server.rev, ...cached.streams.filter((s) => !isThreadStream(s.streamId) && visible.has(s.streamId)).map((s) => s.rev ?? 0)];
    // Unknown counts as 0 (everything may have changed): the room then re-sends changes, or starts the channel over.
    this.rev = Math.min(...revs.map((r) => r ?? 0));
    this.emit({ me: this.userId, categories: server.categories, channels: server.channels, byChannel, read: server.read, mentionCounts: server.mentionCounts, notify: server.notify, mutes: server.mutes ?? {}, dmClosed: server.dmClosed ?? {}, serverNotify: server.serverNotify ?? {}, emojis: server.emojis ?? [], stickers: server.stickers ?? [] });
    // Nothing changed yet: don't write it straight back.
    this.dirty.clear();
    this.serverDirty = false;
  }

  private scheduleSave() {
    if (!this.persisting || this.saveTimer !== undefined) return;
    this.saveTimer = window.setTimeout(this.saveNow, SAVE_DELAY_MS);
  }

  /** Writes what changed to the device cache: the newest messages of each changed conversation, and the layout. */
  private saveNow = () => {
    clearTimeout(this.saveTimer);
    this.saveTimer = undefined;
    if (!this.persisting || !this.snap.me) return;
    const streams: Omit<CachedStream, "key">[] = [];
    for (const id of this.dirty) {
      const ch = this.snap.byChannel[id];
      if (!ch) continue;
      // Only what the room confirmed: pending sends live in the outbox, not on disk.
      const held = ch.messages.filter((m) => !m.state);
      if (!held.length) continue;
      const messages = held.slice(-CACHE_MESSAGES).map(({ state: _s, error: _e, ...m }) => m);
      const entry: Omit<CachedStream, "key"> = { streamId: id, messages, more: ch.more || messages.length < held.length, rev: this.rev };
      if (isThreadStream(id)) {
        // A thread is kept only while we know which channel it hangs off (it goes when that channel does).
        const parentId = threadParentId(id);
        const parent = Object.entries(this.snap.byChannel).find(([cid, c]) => !isThreadStream(cid) && c.messages.some((m) => m.id === parentId))?.[0] ?? this.threadCache.get(id)?.parent;
        if (!parent) continue;
        entry.parent = parent;
        this.threadCache.set(id, { ...entry, key: "" });
      }
      streams.push(entry);
    }
    const { categories, channels, read, mentionCounts, notify, mutes, dmClosed, serverNotify, emojis, stickers } = this.snap;
    const change = {
      server: this.serverDirty && this.snap.channels.length ? { categories, channels, read, mentionCounts, notify, mutes, dmClosed, serverNotify, emojis, stickers, rev: this.rev } : undefined,
      streams,
      drop: [...this.drop],
    };
    this.dirty.clear();
    this.drop.clear();
    if (change.server) this.serverDirty = false;
    if (change.server || streams.length || change.drop.length) void save(this.userId, this.serverId, change);
  };

  /** Removes conversations from memory and from the device (a DM I'm no longer in, a channel that's gone). */
  private forget(ids: string[]) {
    if (!ids.length) return;
    const byChannel = { ...this.snap.byChannel };
    for (const id of ids) {
      delete byChannel[id];
      this.dirty.delete(id);
      this.drop.add(id);
      for (const [tid, t] of this.threadCache) {
        if (t.parent === id) {
          this.threadCache.delete(tid);
          this.drop.add(tid);
        }
      }
    }
    this.emit({ byChannel });
    this.scheduleSave();
  }

  /** Channels I can no longer see (left a DM, a channel was removed): drop what we held of them. */
  private pruneTo(channels: Channel[]) {
    const visible = new Set(channels.map((c) => c.id));
    this.forget(Object.keys(this.snap.byChannel).filter((id) => !isThreadStream(id) && !visible.has(id)));
    const orphans = [...this.threadCache.values()].filter((t) => t.parent && !visible.has(t.parent)).map((t) => t.streamId);
    for (const id of orphans) {
      this.threadCache.delete(id);
      this.drop.add(id);
    }
  }

  /** The member list was fetched: keep it for the next start. */
  saveMembers(members: Member[]) {
    this.cachedMembers = members;
    if (this.persisting) void saveMembers(this.userId, this.serverId, members);
  }

  /** The channel on screen (caught up first on reconnect, prefetched first). */
  setFocus(channelId: string | undefined) {
    this.focus = channelId;
  }

  // ---- Connection ----
  private connect() {
    if (this.stopped) return;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${location.host}/api/servers/${this.serverId}/ws`);
    this.ws = ws;
    this.connectStartedAt = performance.now();
    this.emit({ status: this.snap.me ? "connecting" : "connecting" });
    ws.onopen = async () => {
      this.lastHeard = performance.now();
      // The cached cursors are needed for hello (reading them takes a few ms; the socket usually takes longer).
      await this.loaded;
      if (this.ws !== ws) return;
      // Resume: tell the room the newest message we hold in each channel; it sends exactly what we missed, and
      // (with our change counter) the messages we hold that changed meanwhile.
      const cursors: Record<string, number> = {};
      for (const [id, ch] of Object.entries(this.snap.byChannel)) {
        const last = ch.messages.findLast((m) => !m.state);
        if (last) cursors[id] = last.seq;
      }
      this.raw({ t: "hello", cursors, ...(this.rev !== undefined ? { rev: this.rev } : {}), ...(this.focus ? { focus: this.focus } : {}) });
    };
    ws.onmessage = (e) => {
      this.lastHeard = performance.now();
      this.heardCount++;
      if (e.data === "pong") return; // keep-alive, answered by the room without waking it
      this.onEvent(JSON.parse(e.data as string) as ServerEvent);
    };
    ws.onclose = (e) => this.dropped(ws, e.code);
  }

  /** The socket is gone (closed, or found dead): show offline and schedule a reconnect. */
  private dropped(ws: WebSocket, code: number) {
    if (this.ws !== ws) return;
    this.ws = null;
    clearInterval(this.pingTimer);
    this.closedAt ??= performance.now();
    this.emit({ status: "offline", online: new Set(), synced: false });
    // Sends wait for the next "ready" with their clocks stopped, so a tunnel doesn't turn them into failures (#514).
    this.stopSendClocks();
    if (code === 4003) {
      // Removed from the server: nothing of it stays on this device.
      this.stopped = true;
      this.persisting = false;
      clearTimeout(this.saveTimer);
      void wipeServer(this.userId, this.serverId);
      return;
    }
    if (code === 4001) {
      // This sign-in ended (signed out here or elsewhere, or the password was reset): back to the sign-in page.
      this.stopped = true;
      dispatchEvent(new Event(SIGNED_OUT_EVENT));
      return;
    }
    // Back off 0.25 s, 0.5 s, 1 s … up to 10 s, with jitter, so a room restart doesn't get a thundering herd.
    const delay = Math.min(10_000, 250 * 2 ** this.attempt++) * (0.75 + Math.random() / 2);
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }


  /**
   * A dropped network often leaves the socket looking open for minutes. Every 15 s we send a tiny "ping" that
   * the room answers without waking up; if nothing at all has arrived for 32 s, the socket is dead: reconnect.
   */
  private checkAlive() {
    const ws = this.ws;
    if (!ws) return;
    const now = performance.now();
    // A socket stuck connecting never fires onclose on some links: give up on it and try again (#514).
    if (ws.readyState === WebSocket.CONNECTING) {
      if (now - this.connectStartedAt > CONNECT_TIMEOUT_MS) this.kill(ws);
      return;
    }
    if (ws.readyState !== WebSocket.OPEN) return;
    if (now - this.lastHeard > 32_000) return this.kill(ws);
    if (now - this.lastBeat >= 15_000) {
      this.lastBeat = now;
      ws.send("ping");
    }
  }

  /** Treat a socket as dead right away (e.g. the browser says we went offline). */
  private kill(ws: WebSocket) {
    try {
      ws.close(4000, "dead");
    } catch {
      // ignore
    }
    this.dropped(ws, 4000);
  }

  private wentOffline = () => {
    if (this.ws) this.kill(this.ws);
  };

  /** After sleep or a long time in the background: check the socket answers within 3 s, else reconnect. */
  private probe() {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return this.wake();
    const sent = performance.now();
    ws.send("ping");
    window.setTimeout(() => {
      if (this.ws === ws && this.lastHeard < sent) this.kill(ws);
    }, 3_000);
  }

  /** Reconnect immediately when the network or the tab comes back, instead of waiting out the backoff. */
  private wake = () => {
    if (this.stopped || this.ws) return;
    clearTimeout(this.reconnectTimer);
    this.attempt = 0;
    this.connect();
  };
  private onVisible = () => {
    if (document.visibilityState === "visible") this.probe();
    else {
      // The tab may be about to be frozen or closed.
      this.flushReads();
      this.saveNow();
    }
    this.reportActive();
  };

  /**
   * Tells the room whether I'm actually here: page visible and window focused, and when I last touched it. The room
   * decides when that counts as idle (my setting), so a laptop left open on Bloom doesn't swallow my phone's push.
   */
  private reportActive = () => this.active.check();
  private onInput = () => this.active.input();

  close() {
    this.flushReads();
    this.saveNow();
    this.stopped = true;
    removeEventListener("pagehide", this.saveNow);
    clearInterval(this.pingTimer);
    clearTimeout(this.reconnectTimer);
    clearInterval(this.heartbeat);
    removeEventListener("online", this.wake);
    removeEventListener("offline", this.wentOffline);
    document.removeEventListener("visibilitychange", this.onVisible);
    removeEventListener("focus", this.reportActive);
    removeEventListener("blur", this.reportActive);
    for (const t of ACTIVITY) removeEventListener(t, this.onInput, { capture: true });
    this.ws?.close(1000);
  }

  private raw(e: ClientEvent) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(e));
      return true;
    }
    return false;
  }

  private ping() {
    this.raw({ t: "ping", n: performance.now() });
  }

  // ---- Events from the room ----
  private onEvent(e: ServerEvent) {
    switch (e.t) {
      case "ready": {
        this.pendingRev = e.rev;
        this.pruneTo(e.channels);
        const byChannel = { ...this.snap.byChannel };
        for (const c of e.channels) byChannel[c.id] ??= emptyChannel();
        // Reads made here while offline still count: keep whichever position is further along.
        const read = { ...e.read };
        const mentionCounts = { ...e.mentionCounts };
        for (const [id, seq] of this.unsentReads) {
          if (seq <= (read[id] ?? 0)) continue;
          read[id] = seq;
          if (seq >= (e.channels.find((c) => c.id === id)?.lastSeq ?? 0)) delete mentionCounts[id];
        }
        this.emit({ status: "live", synced: false, me: e.you, categories: e.categories, channels: e.channels.map(withWeekSeq), byChannel, online: new Set(e.online), read, mentionCounts, notify: e.notify ?? {}, mutes: e.mutes ?? {}, dmClosed: e.dmClosed ?? {}, serverNotify: e.serverNotify ?? {}, apps: e.apps ?? [], privateCards: e.privateCards ?? [], emojis: e.emojis ?? [], stickers: e.stickers ?? [], lists: e.lists ?? [], privateItems: e.privateItems ?? {}, blooming: e.blooming ?? [], bloomWho: e.bloomWho ?? "everyone", integrations: e.integrations ?? [], bots: e.bots ?? [] });
        // App data being watched: subscribe again on this socket and reload what may have changed meanwhile.
        for (const k of this.watching.keys()) this.startWatch(k);
        this.flushReads();
        // A new socket starts out inactive in the room.
        this.active.reset();
        this.reportActive();
        if (this.closedAt !== null) record("reconnect", performance.now() - this.closedAt);
        this.closedAt = null;
        this.attempt = 0;
        // Anything still unconfirmed goes again; the room dedupes by id, so nothing posts twice. The 15 s clock
        // starts now, not when the person pressed send (#514).
        for (const s of this.outbox) {
          this.raw(s);
          this.startSendClock(s.id);
        }
        // Open threads aren't in the backfill: refresh their newest page.
        for (const id of Object.keys(byChannel)) if (isThreadStream(id)) this.raw({ t: "history", rid: String(++this.rid), channelId: id });
        this.ping();
        this.pingTimer = window.setInterval(() => this.ping(), 30_000);
        return;
      }
      case "backfill": {
        const ch = this.snap.byChannel[e.channelId] ?? emptyChannel();
        const pending = ch.messages.filter((m) => m.state);
        // `more` means we fell too far behind (or the cache is stale), `reset` that what we held doesn't fit: start
        // this channel fresh from the latest page.
        const kept = e.more || e.reset ? [] : ch.messages.filter((m) => !m.state);
        const messages = mergeBySeq(kept, e.messages).concat(pending);
        // Whether older messages exist: `more` says so. So does a first confirmed message above seq 1: a page that only
        // adds to what we hold says nothing about what lies above it, and a cache the older build wrote may have lost
        // the flag (#234).
        this.setChannel(e.channelId, { messages, more: e.more || (messages.find((m) => !m.state)?.seq ?? 1) > 1 });
        return;
      }
      case "changed": {
        // Messages we hold that changed while we were away: swap in the room's version.
        const ch = this.snap.byChannel[e.channelId];
        if (!ch) return;
        const fresh = new Map(e.messages.map((m) => [m.id, m]));
        this.setChannel(e.channelId, { messages: ch.messages.map((m) => fresh.get(m.id) ?? m) });
        return;
      }
      case "synced": {
        // The whole catch-up has arrived, so what we hold is now as fresh as the counter from "ready".
        if (this.pendingRev !== undefined) this.rev = this.pendingRev;
        this.serverDirty = true;
        this.emit({ synced: true });
        if (!this.coldLiveRecorded) {
          this.coldLiveRecorded = true;
          record("cold_start_live", performance.now());
        }
        this.prefetch();
        return;
      }
      case "msg": {
        // Thread replies only matter if that thread is open here; its summary arrives as a "thread" event.
        if (isThreadStream(e.m.channelId) && !this.snap.byChannel[e.m.channelId]) return;
        const ch = this.snap.byChannel[e.m.channelId] ?? emptyChannel();
        if (ch.messages.some((m) => m.id === e.m.id)) return;
        const confirmed = ch.messages.filter((m) => !m.state);
        const pending = ch.messages.filter((m) => m.state);
        this.setChannel(e.m.channelId, { messages: mergeBySeq(confirmed, [e.m]).concat(pending) });
        record("echo", Date.now() + this.clockOffset - e.m.ts);
        this.clearTyping(e.m.channelId, e.m.authorId);
        this.bumpChannel(e.m.channelId, e.m.seq);
        if (!isThreadStream(e.m.channelId)) {
          // Mine (from another device): I've read up to it. Someone else's that mentions me: one more badge.
          if (e.m.authorId === this.snap.me) this.applyRead(e.m.channelId, e.m.seq);
          else if (e.m.seq > (this.snap.read[e.m.channelId] ?? 0) && countsAsMention(e.m, this.snap.me, this.snap.channels.find((c) => c.id === e.m.channelId))) {
            this.emit({ mentionCounts: { ...this.snap.mentionCounts, [e.m.channelId]: (this.snap.mentionCounts[e.m.channelId] ?? 0) + 1 } });
          }
        }
        return;
      }
      case "ack": {
        const t0 = this.sentAt.get(e.id);
        if (t0 !== undefined) record("send_ack", performance.now() - t0);
        this.sentAt.delete(e.id);
        this.stopSendClock(e.id);
        this.outbox = this.outbox.filter((s) => s.id !== e.id);
        const ch = this.snap.byChannel[e.channelId];
        if (!ch) return;
        const mine = ch.messages.find((m) => m.id === e.id);
        if (!mine) return;
        const done: ChatMessage = { ...mine, seq: e.seq, ts: e.ts, state: undefined, replyTo: e.replyTo ?? mine.replyTo, body: e.body ?? mine.body, forward: e.forward ?? mine.forward };
        const confirmed = ch.messages.filter((m) => !m.state);
        const pending = ch.messages.filter((m) => m.state && m.id !== e.id);
        this.setChannel(e.channelId, { messages: mergeBySeq(confirmed, [done]).concat(pending) });
        this.bumpChannel(e.channelId, e.seq);
        // The room moved my read position to my own message; do the same here.
        if (!isThreadStream(e.channelId)) this.applyRead(e.channelId, e.seq);
        return;
      }
      case "history": {
        const ch = this.snap.byChannel[e.channelId] ?? emptyChannel();
        const confirmed = ch.messages.filter((m) => !m.state);
        const pending = ch.messages.filter((m) => m.state);
        // The room's copy wins over ours (it may be fresher than a cached one). A page that doesn't touch what we
        // hold (a thread that grew by more than a page since we cached it) can't be stitched on: start from it.
        const first = e.messages[0]?.seq;
        const last = e.messages.at(-1)?.seq;
        const disjoint = first !== undefined && last !== undefined && confirmed.length > 0 && (first > confirmed.at(-1)!.seq + 1 || last < confirmed[0]!.seq - 1);
        const messages = mergeBySeq(disjoint ? [] : confirmed, e.messages);
        // Whether older messages exist above the first one we now hold: the room knows if its page is the oldest part.
        const more = !e.messages.length ? false : messages[0]?.seq === first ? e.more : ch.more;
        this.setChannel(e.channelId, { messages: messages.concat(pending), more, loadingOlder: false });
        this.pendingRequests.get(e.rid)?.(e);
        this.pendingRequests.delete(e.rid);
        return;
      }
      case "reaction": {
        this.applyReaction(e.channelId, e.messageId, e.emoji, e.userId, e.on, e.jumbo === true);
        // A jumbo reaction (#714) lands with a thud, here and now, instead of a super effect; both are live only.
        if (e.on && e.jumbo === true) playJumboLanding(e.messageId, e.emoji);
        // A super reaction (#87): played once, here and now; the effect is never stored.
        else if (e.on && isReactionEffect(e.effect)) playReactionEffect(e.messageId, e.emoji, e.effect);
        return;
      }
      case "edited": {
        this.refs.delete(e.messageId); // a message link's next preview asks again
        const ch = this.snap.byChannel[e.channelId];
        if (!ch) return;
        // An edit can add or drop a mention of me in an unread message: keep the badge right.
        const before = ch.messages.find((m) => m.id === e.messageId);
        if (before && before.seq > (this.snap.read[e.channelId] ?? 0)) {
          const channel = this.snap.channels.find((c) => c.id === e.channelId);
          const was = countsAsMention(before, this.snap.me, channel);
          const now = countsAsMention({ ...before, mentions: e.mentions }, this.snap.me, channel);
          if (was !== now) {
            const n = Math.max(0, (this.snap.mentionCounts[e.channelId] ?? 0) + (now ? 1 : -1));
            const mentionCounts = { ...this.snap.mentionCounts, [e.channelId]: n };
            if (!n) delete mentionCounts[e.channelId];
            this.emit({ mentionCounts });
          }
        }
        this.setChannel(e.channelId, { messages: ch.messages.map((m) => (m.id === e.messageId ? { ...m, body: e.body, editedAt: e.editedAt, mentions: e.mentions } : m)) });
        return;
      }
      case "stream": {
        // @Bloom's reply growing (the whole text so far), or finished (`done`: the stored text and its mentions).
        const ch = this.snap.byChannel[e.channelId];
        if (!ch) return;
        this.setChannel(e.channelId, {
          messages: ch.messages.map((m) => {
            if (m.id !== e.messageId) return m;
            if (!e.done) return { ...m, body: e.body, streaming: true };
            const { streaming: _s, ...rest } = m;
            return { ...rest, body: e.body, mentions: e.mentions, ...(e.card ? { card: e.card } : {}) };
          }),
        });
        return;
      }
      case "card": {
        // "+N more" folded into a card (the per-channel rate limit).
        const ch = this.snap.byChannel[e.channelId];
        if (!ch) return;
        this.setChannel(e.channelId, { messages: ch.messages.map((m) => (m.id === e.messageId ? { ...m, card: e.card } : m)) });
        return;
      }
      case "embeds": {
        const ch = this.snap.byChannel[e.channelId];
        if (!ch) return;
        this.setChannel(e.channelId, { messages: ch.messages.map((m) => (m.id === e.messageId ? { ...m, embeds: e.embeds.length ? e.embeds : undefined } : m)) });
        return;
      }
      case "attachments": {
        const ch = this.snap.byChannel[e.channelId];
        if (!ch) return;
        this.setChannel(e.channelId, { messages: ch.messages.map((m) => (m.id === e.messageId ? { ...m, attachments: e.attachments.length ? e.attachments : undefined } : m)) });
        return;
      }
      case "deleted": {
        this.refs.delete(e.messageId); // a message link's next preview asks again
        const ch = this.snap.byChannel[e.channelId];
        if (!ch) return;
        // An unread mention that's deleted stops counting.
        const gone = ch.messages.find((m) => m.id === e.messageId);
        const count = this.snap.mentionCounts[e.channelId] ?? 0;
        if (gone && !gone.deleted && count > 0 && gone.authorId !== this.snap.me && gone.seq > (this.snap.read[e.channelId] ?? 0) && countsAsMention(gone, this.snap.me, this.snap.channels.find((c) => c.id === e.channelId))) {
          this.emit({ mentionCounts: { ...this.snap.mentionCounts, [e.channelId]: count - 1 } });
        }
        this.setChannel(e.channelId, {
          messages: ch.messages.map((m) => (m.id === e.messageId ? { ...m, deleted: true, body: "", attachments: undefined, reactions: undefined, jumbo: undefined, embeds: undefined } : m)),
        });
        return;
      }
      case "thread": {
        const ch = this.snap.byChannel[e.channelId];
        if (!ch?.messages.some((m) => m.id === e.parentId)) return;
        this.setChannel(e.channelId, { messages: ch.messages.map((m) => (m.id === e.parentId ? { ...m, thread: e.thread } : m)) });
        return;
      }
      case "typing": {
        record("typing", Date.now() + this.clockOffset - e.ts);
        const typing = { ...this.snap.typing, [e.channelId]: { ...this.snap.typing[e.channelId], [e.userId]: Date.now() + TYPING_TTL_MS } };
        this.emit({ typing });
        clearTimeout(this.typingTimer);
        this.typingTimer = window.setTimeout(() => this.expireTyping(), TYPING_TTL_MS + 50);
        return;
      }
      case "presence": {
        const online = new Set(this.snap.online);
        if (e.online) online.add(e.userId);
        else online.delete(e.userId);
        this.emit({ online });
        return;
      }
      case "channel": {
        if (e.rid) {
          this.pendingRequests.get(e.rid)?.(e);
          this.pendingRequests.delete(e.rid);
        }
        // A DM I'm no longer in (I was removed from it): it goes, from the list and from this device.
        if (e.channel.kind === "dm" && this.snap.me && !e.channel.members?.includes(this.snap.me)) {
          this.emit({ channels: this.snap.channels.filter((c) => c.id !== e.channel.id) });
          this.forget([e.channel.id]);
          return;
        }
        if (this.snap.channels.some((c) => c.id === e.channel.id)) {
          // A change to a channel we have (a rename): keep our own lastSeq, which may be newer.
          this.emit({ channels: this.snap.channels.map((c) => (c.id === e.channel.id ? { ...withWeekSeq(e.channel), lastSeq: c.lastSeq } : c)) });
          return;
        }
        const channels = [...this.snap.channels, withWeekSeq(e.channel)].sort((a, b) => a.position - b.position);
        this.emit({ channels, byChannel: { ...this.snap.byChannel, [e.channel.id]: emptyChannel() } });
        return;
      }
      case "category": {
        if (e.rid) {
          this.pendingRequests.get(e.rid)?.(e);
          this.pendingRequests.delete(e.rid);
        }
        if (this.snap.categories.some((c) => c.id === e.category.id)) {
          // Renamed.
          this.emit({ categories: this.snap.categories.map((c) => (c.id === e.category.id ? e.category : c)) });
          return;
        }
        this.emit({ categories: [...this.snap.categories, e.category].sort((a, b) => a.position - b.position) });
        return;
      }
      case "structure": {
        if (e.rid) this.answer(e.rid, e);
        this.pruneTo(e.channels);
        const byChannel = { ...this.snap.byChannel };
        for (const c of e.channels) byChannel[c.id] ??= emptyChannel();
        // Keep our own lastSeq, which may be newer than the room's copy at the time it sent this.
        const seqs = new Map(this.snap.channels.map((c) => [c.id, c.lastSeq]));
        const channels = e.channels.map((c) => ({ ...withWeekSeq(c), lastSeq: Math.max(c.lastSeq, seqs.get(c.id) ?? 0) }));
        this.emit({ categories: e.categories, channels, byChannel });
        return;
      }
      case "channelDeleted": {
        if (e.rid) this.answer(e.rid, e);
        // Everything we hold about it goes (anyone viewing it is moved on by the page, which no longer finds it).
        const drop = <T,>(r: Record<string, T>) => {
          const { [e.channelId]: _, ...rest } = r;
          return rest;
        };
        const byChannel = drop(this.snap.byChannel);
        // Open threads under its messages go too.
        for (const m of this.snap.byChannel[e.channelId]?.messages ?? []) delete byChannel[threadStreamId(m.id)];
        this.unsentReads.delete(e.channelId);
        this.emit({
          channels: this.snap.channels.filter((c) => c.id !== e.channelId),
          byChannel,
          read: drop(this.snap.read),
          mentionCounts: drop(this.snap.mentionCounts),
          notify: drop(this.snap.notify),
          mutes: drop(this.snap.mutes),
          dmClosed: drop(this.snap.dmClosed),
          typing: drop(this.snap.typing),
          // Private cards in it (or in its threads) went with it in the room.
          privateCards: this.snap.privateCards.filter((c) => c.stream !== e.channelId && !(isThreadStream(c.stream) && !byChannel[c.stream] && (this.snap.byChannel[e.channelId]?.messages ?? []).some((m) => m.id === threadParentId(c.stream)))),
        });
        // …and from the device cache (with its threads).
        this.forget([e.channelId]);
        return;
      }
      case "categoryDeleted": {
        if (e.rid) this.answer(e.rid, e);
        // Its channels' new places arrive in the "structure" event right after.
        this.emit({ categories: this.snap.categories.filter((c) => c.id !== e.categoryId) });
        return;
      }
      case "customEmoji": {
        this.emit({ emojis: e.emojis, stickers: e.stickers });
        return;
      }
      case "server":
      case "role":
      case "memberLeft": {
        for (const fn of this.serverListeners) fn(e);
        return;
      }
      case "read": {
        this.applyRead(e.channelId, e.seq);
        return;
      }
      case "unread": {
        this.unsentReads.delete(e.channelId);
        const mentionCounts = { ...this.snap.mentionCounts, [e.channelId]: e.mentions };
        if (!e.mentions) delete mentionCounts[e.channelId];
        this.emit({ read: { ...this.snap.read, [e.channelId]: e.seq }, mentionCounts });
        return;
      }
      case "notifyPref": {
        this.emit({ notify: withKey(this.snap.notify, e.channelId, e.level) });
        return;
      }
      case "dmClose": {
        this.emit({ dmClosed: withKey(this.snap.dmClosed, e.channelId, e.seq) });
        return;
      }
      case "notifyMute": {
        if (e.channelId === undefined) this.emit({ serverNotify: { ...this.snap.serverNotify, mutedUntil: e.until ?? undefined } });
        else this.emit({ mutes: withKey(this.snap.mutes, e.channelId, e.until) });
        return;
      }
      case "serverNotify": {
        this.emit({ serverNotify: e.settings });
        return;
      }
      case "memberUpdated": {
        rememberAvatars([e.member]);
        for (const fn of this.memberListeners) fn(e.member);
        return;
      }
      case "privateCard": {
        const rest = this.snap.privateCards.filter((c) => c.id !== e.card.id);
        const had = rest.length !== this.snap.privateCards.length;
        this.emit({ privateCards: had ? this.snap.privateCards.map((c) => (c.id === e.card.id ? e.card : c)) : [...rest, e.card] });
        return;
      }
      case "privateCardGone": {
        this.emit({ privateCards: this.snap.privateCards.filter((c) => c.id !== e.id) });
        return;
      }
      case "appChange": {
        this.appData.apply(e.app, e.collection, e.id, e.record);
        return;
      }
      case "appRecords": {
        this.appData.setAll(e.app, e.collection, e.records);
        this.answer(e.rid, e);
        return;
      }
      case "list": {
        this.upsertList(e.list);
        if (e.rid) this.answer(e.rid, e);
        return;
      }
      case "listDeleted": {
        const { [e.listId]: _gone, ...privateItems } = this.snap.privateItems;
        this.emit({ lists: this.snap.lists.filter((l) => l.id !== e.listId), privateItems });
        this.patchMessages((m) => (m.lists?.includes(e.listId) ? { ...m, lists: listsOrNone(m.lists.filter((id) => id !== e.listId)) } : m));
        if (e.rid) this.answer(e.rid, e);
        return;
      }
      case "listItem": {
        this.applyListItem(e.item, e.on, e.list, e.lists);
        if (e.rid) this.answer(e.rid, e);
        return;
      }
      case "listItems":
      case "searchResults":
      case "messageRef":
      case "appHistory":
      case "appResult":
      case "integrationResult":
      case "commandResult":
      case "bloomResult":
      case "bloomVersions": {
        this.answer(e.rid, e);
        return;
      }
      case "apps": {
        this.emit({ apps: e.apps });
        return;
      }
      case "integrations": {
        this.emit({ integrations: e.integrations });
        return;
      }
      case "blooming": {
        const has = this.snap.blooming.some((t) => t.id === e.thread.id);
        this.emit({ blooming: has ? this.snap.blooming.map((t) => (t.id === e.thread.id ? e.thread : t)) : [...this.snap.blooming, e.thread] });
        return;
      }
      case "bloomingDeleted": {
        this.emit({ blooming: this.snap.blooming.filter((t) => t.id !== e.threadId) });
        return;
      }
      case "bot": {
        const has = this.snap.bots.some((b) => b.app === e.bot.app);
        this.emit({ bots: has ? this.snap.bots.map((b) => (b.app === e.bot.app ? e.bot : b)) : [...this.snap.bots, e.bot] });
        break;
      }
      case "bloomWho": {
        this.emit({ bloomWho: e.who });
        return;
      }
      case "pong": {
        const now = performance.now();
        const rtt = now - e.n;
        record("ping", rtt);
        // Room time at the midpoint of the round trip, compared with our clock.
        this.clockOffset = e.ts - (Date.now() - rtt / 2);
        return;
      }
      case "error": {
        // A refused delete, edit or reaction names no message: say what the room said (#226), as the native apps do.
        // Not "no_collection": a card's background appSub, sent again on every "ready", is refused for a collection
        // I can't see, which I never asked for. A failed send (id) shows Retry; an answer to a request (rid) goes to
        // whoever asked.
        const text = e.message?.trim();
        if (!e.id && !e.rid && e.code !== "no_collection" && text) this.emit({ notice: { n: (this.snap.notice?.n ?? 0) + 1, text } });
        if (e.id) this.fail(e.id, e.message);
        if (e.rid) {
          this.pendingRequests.get(e.rid)?.(e);
          this.pendingRequests.delete(e.rid);
        }
        return;
      }
    }
  }

  // ---- Lists (shared/src/lists.ts) ----

  private upsertList(list: ListInfo) {
    const has = this.snap.lists.some((l) => l.id === list.id);
    const lists = has ? this.snap.lists.map((l) => (l.id === list.id ? list : l)) : [...this.snap.lists, list];
    const patch: Partial<Snapshot> = { lists };
    if (list.visibility === "private" && !this.snap.privateItems[list.id]) patch.privateItems = { ...this.snap.privateItems, [list.id]: [] };
    this.emit(patch);
  }

  /** Rewrites every held copy of matching messages (a message can be held in its stream only, but threads too). */
  private patchMessages(fn: (m: ChatMessage) => ChatMessage, only?: string) {
    const byChannel = { ...this.snap.byChannel };
    let changed = false;
    for (const [id, ch] of Object.entries(byChannel)) {
      if (only && !ch.messages.some((m) => m.id === only)) continue;
      let touched = false;
      const messages = ch.messages.map((m) => {
        const next = fn(m);
        if (next !== m) touched = true;
        return next;
      });
      if (touched) {
        byChannel[id] = { ...ch, messages };
        changed = true;
      }
    }
    if (changed) this.emit({ byChannel });
  }

  private applyListItem(item: ListItem, on: boolean, list: ListInfo, lists?: string[]) {
    this.upsertList(list);
    if (lists !== undefined) this.patchMessages((m) => (m.id === item.messageId ? { ...m, lists: listsOrNone(lists) } : m), item.messageId);
    if (list.visibility === "private") {
      const now = (this.snap.privateItems[list.id] ?? []).filter((id) => id !== item.messageId);
      this.emit({ privateItems: { ...this.snap.privateItems, [list.id]: on ? [item.messageId, ...now] : now } });
    }
  }

  /** The list ids a message is in that I can see: its shared lists, and my private lists holding it. */
  listsOf(m: Pick<Message, "id" | "lists">): string[] {
    const mine = Object.entries(this.snap.privateItems).filter(([, ids]) => ids.includes(m.id)).map(([id]) => id);
    return [...(m.lists ?? []), ...mine];
  }

  /** The pins list of a channel, if anything was ever pinned there. */
  pinsOf(channelId: string): ListInfo | undefined {
    return this.snap.lists.find((l) => l.kind === "pins" && l.channelId === channelId);
  }

  /**
   * Puts a message in a list or takes it out (listId may be PINS), showing it at once. Resolves with an error
   * message (the change is undone here), or null.
   */
  async setListItem(m: Pick<Message, "id" | "channelId">, listId: string, on: boolean): Promise<string | null> {
    const root = isThreadStream(m.channelId) ? this.rootOf(m.channelId) : m.channelId;
    const list = listId === PINS ? (root ? this.pinsOf(root) : undefined) : this.snap.lists.find((l) => l.id === listId);
    const before = { privateItems: this.snap.privateItems, held: this.heldLists(m.id) };
    if (list?.visibility === "private") {
      const now = (this.snap.privateItems[list.id] ?? []).filter((id) => id !== m.id);
      this.emit({ privateItems: { ...this.snap.privateItems, [list.id]: on ? [m.id, ...now] : now } });
    } else if (list) {
      this.patchMessages((x) => {
        if (x.id !== m.id) return x;
        const rest = (x.lists ?? []).filter((id) => id !== list.id);
        return { ...x, lists: listsOrNone(on ? [...rest, list.id] : rest) };
      }, m.id);
    }
    const r = await this.ask("listItem", { t: "listItem", rid: String(++this.rid), listId, messageId: m.id, on });
    if (r.t === "listItem") return null;
    // Put back what we showed.
    this.emit({ privateItems: before.privateItems });
    if (before.held !== null) this.patchMessages((x) => (x.id === m.id ? { ...x, lists: listsOrNone(before.held!) } : x), m.id);
    return r.message;
  }

  /** The shared lists of the held copy of a message (null: not held). */
  private heldLists(messageId: string): string[] | null {
    for (const ch of Object.values(this.snap.byChannel)) {
      const m = ch.messages.find((x) => x.id === messageId);
      if (m) return m.lists ?? [];
    }
    return null;
  }

  /** The channel a thread hangs off, if we hold its parent message. */
  rootOf(stream: string): string | undefined {
    if (!isThreadStream(stream)) return stream;
    const parentId = threadParentId(stream);
    return Object.entries(this.snap.byChannel).find(([id, c]) => !isThreadStream(id) && c.messages.some((m) => m.id === parentId))?.[0];
  }

  async createList(name: string, visibility: ListVisibility, emoji = ""): Promise<{ ok: true; list: ListInfo } | { ok: false; message: string }> {
    const r = await this.ask("list", { t: "createList", rid: String(++this.rid), name, emoji, visibility });
    return r.t === "list" ? { ok: true, list: r.list } : { ok: false, message: r.message };
  }

  async updateList(listId: string, patch: { name?: string; emoji?: string; addPolicy?: "everyone" | "chosen"; adders?: string[] }): Promise<string | null> {
    const r = await this.ask("list", { t: "updateList", rid: String(++this.rid), listId, ...patch });
    return r.t === "list" ? null : r.message;
  }

  async deleteList(listId: string): Promise<string | null> {
    const r = await this.ask("listDeleted", { t: "deleteList", rid: String(++this.rid), listId });
    return r.t === "listDeleted" ? null : r.message;
  }

  /** A page of a list (newest added first), from before `before` (an addedAt). */
  async listItems(listId: string, before?: number): Promise<{ items: ListItem[]; messages: Message[]; more: boolean } | { error: string }> {
    const r = await this.ask("listItems", { t: "listItems", rid: String(++this.rid), listId, ...(before ? { before } : {}) });
    return r.t === "listItems" ? { items: r.items, messages: r.messages, more: r.more } : { error: r.message };
  }

  /** Messages with every word of `q`, newest first (before `before`, a ts), optionally in one channel or DM, narrowed by `filters`. */
  async search(q: string, opts: { channelId?: string; before?: number; filters?: SearchFilters } = {}): Promise<{ hits: SearchHit[]; messages: Message[]; more: boolean } | { error: string }> {
    const r = await this.ask("searchResults", { t: "search", rid: String(++this.rid), q, ...opts });
    return r.t === "searchResults" ? { hits: r.hits, messages: r.messages, more: r.more } : { error: r.message };
  }

  /** Hands a reply to whoever is waiting on that request id. */
  private answer(rid: string, e: ServerEvent) {
    this.pendingRequests.get(rid)?.(e);
    this.pendingRequests.delete(rid);
  }

  private setChannel(id: string, patch: Partial<ChannelState>) {
    const prev = this.snap.byChannel[id] ?? emptyChannel();
    this.emit({ byChannel: { ...this.snap.byChannel, [id]: { ...prev, ...patch } } });
  }

  private bumpChannel(id: string, seq: number) {
    const channels = this.snap.channels.map((c) => (c.id === id && seq > c.lastSeq ? { ...c, lastSeq: seq } : c));
    this.emit({ channels });
  }

  /**
   * Moves my read position forward locally (never back) and recounts the mention badge: zero once I've read to
   * the newest message, otherwise the unread mentions among the messages held here (never more than before).
   */
  private applyRead(channelId: string, seq: number) {
    if (seq <= (this.snap.read[channelId] ?? 0)) return;
    const read = { ...this.snap.read, [channelId]: seq };
    const mentionCounts = { ...this.snap.mentionCounts };
    const channel = this.snap.channels.find((c) => c.id === channelId);
    if (mentionCounts[channelId]) {
      if (!channel || seq >= channel.lastSeq) delete mentionCounts[channelId];
      else {
        // We hold the newest messages without gaps, so if we reach back to the new position we can recount exactly;
        // otherwise keep the old count until the next "ready".
        const held = (this.snap.byChannel[channelId]?.messages ?? []).filter((m) => !m.state);
        if (held.some((m) => m.seq <= seq + 1)) {
          const left = held.filter((m) => !m.deleted && m.seq > seq && m.authorId !== this.snap.me && countsAsMention(m, this.snap.me, channel)).length;
          if (left) mentionCounts[channelId] = left;
          else delete mentionCounts[channelId];
        }
      }
    }
    this.emit({ read, mentionCounts });
  }

  private fail(id: string, error: string) {
    this.outbox = this.outbox.filter((s) => s.id !== id);
    this.stopSendClock(id);
    for (const [cid, ch] of Object.entries(this.snap.byChannel)) {
      if (ch.messages.some((m) => m.id === id)) {
        this.setChannel(cid, { messages: ch.messages.map((m) => (m.id === id ? { ...m, state: "failed", error } : m)) });
      }
    }
  }

  private clearTyping(channelId: string, userId: string) {
    const ch = this.snap.typing[channelId];
    if (!ch?.[userId]) return;
    const { [userId]: _, ...rest } = ch;
    this.emit({ typing: { ...this.snap.typing, [channelId]: rest } });
  }

  private expireTyping() {
    const now = Date.now();
    const typing: Snapshot["typing"] = {};
    let next = Infinity;
    for (const [cid, users] of Object.entries(this.snap.typing)) {
      typing[cid] = {};
      for (const [uid, exp] of Object.entries(users)) {
        if (exp > now) {
          typing[cid][uid] = exp;
          next = Math.min(next, exp);
        }
      }
    }
    this.emit({ typing });
    if (next < Infinity) this.typingTimer = window.setTimeout(() => this.expireTyping(), next - now + 50);
  }

  // ---- Actions ----

  /**
   * I've read `channelId` up to `seq`. Shows it at once; the room hears about it after a short pause, so scrolling
   * through a busy channel sends one frame instead of dozens. Not for threads (they aren't tracked).
   */
  markRead(channelId: string, seq: number) {
    if (isThreadStream(channelId) || seq <= (this.snap.read[channelId] ?? 0)) return;
    this.applyRead(channelId, seq);
    this.unsentReads.set(channelId, Math.max(seq, this.unsentReads.get(channelId) ?? 0));
    clearTimeout(this.readTimer);
    this.readTimer = window.setTimeout(() => this.flushReads(), READ_DEBOUNCE_MS);
  }

  /**
   * Mark unread from the message numbered `seq` (Discord's "Mark Unread"): my read position becomes `seq` − 1, here
   * at once and on my other devices. The room answers with the exact mention count. Not for threads.
   */
  markUnread(channelId: string, seq: number) {
    if (isThreadStream(channelId) || seq <= 0) return;
    this.unsentReads.delete(channelId);
    this.emit({ read: { ...this.snap.read, [channelId]: seq - 1 } });
    this.raw({ t: "unread", channelId, seq });
  }

  /** Sends read positions now (e.g. Esc, or the tab going to the background). Kept for later if offline. */
  flushReads() {
    clearTimeout(this.readTimer);
    for (const [channelId, seq] of this.unsentReads) {
      if (this.raw({ t: "read", channelId, seq })) this.unsentReads.delete(channelId);
    }
  }

  /**
   * Shows the message at once, then sends it. The room's ack swaps in its seq; failures get a retry.
   * channelId may be a thread stream ("t:<parent id>"). replyTo quotes a message in the same stream.
   */
  send(channelId: string, body: string, opts: { replyTo?: ChatMessage; attachments?: Attachment[]; lists?: string[] } = {}) {
    const text = body.trim();
    const attachments = opts.attachments?.length ? opts.attachments : undefined;
    if ((!text && !attachments) || !this.snap.me) return;
    const id = ulid();
    const optimistic: ChatMessage = { id, channelId, seq: Number.MAX_SAFE_INTEGER, authorId: this.snap.me, body: text, ts: Date.now(), state: "pending", attachments };
    if (opts.replyTo) {
      const q = opts.replyTo;
      optimistic.replyTo = { id: q.id, authorId: q.authorId, excerpt: q.body.slice(0, 140), attachments: q.attachments?.length ?? 0 };
    }
    const ch = this.snap.byChannel[channelId] ?? emptyChannel();
    this.setChannel(channelId, { messages: [...ch.messages, optimistic] });
    this.lastTypingSent.delete(channelId);
    this.transmit({ t: "send", id, channelId, body: text, replyTo: opts.replyTo?.id, attachments, ...(opts.lists?.length ? { lists: opts.lists } : {}) });
    // Usage events: what kind of message, never what it says (analytics.ts).
    const where = this.whereKind(channelId);
    track("message_sent", { where, attachments: attachments?.length ?? 0, sticker: !!attachments?.some((a) => a.kind === "sticker"), reply: !!opts.replyTo, forward: false });
    if (/@bloom\b|<@bloom>/i.test(text) || opts.replyTo?.authorId === BLOOM_ID) track("bloom_asked", { where });
  }

  /**
   * Forwards a message I can see to a text channel or DM (Discord's Forward), with an optional note. Shown there at
   * once from my copy of the original; the room snapshots it (copying its files) and its ack swaps in what it stored.
   */
  forward(channelId: string, source: ChatMessage, note = "") {
    if (!this.snap.me || source.deleted || source.system) return;
    const text = note.trim();
    const id = ulid();
    // A forward of a forward with no note of its own passes on the original, as the room does.
    const inner: Forward | undefined = source.forward && !source.body.trim() ? source.forward : undefined;
    const sourceRoot = this.rootOf(source.channelId);
    const dm = this.snap.channels.find((c) => c.id === sourceRoot)?.kind === "dm";
    const forward: Forward = inner ?? {
      channelId: sourceRoot ?? source.channelId,
      stream: source.channelId,
      messageId: source.id,
      ...(dm ? { dm: true as const } : {}),
      ts: source.ts,
      body: source.body,
      ...(source.attachments?.length ? { attachments: source.attachments } : {}),
      ...(source.embeds?.length ? { embeds: source.embeds } : {}),
    };
    const optimistic: ChatMessage = { id, channelId, seq: Number.MAX_SAFE_INTEGER, authorId: this.snap.me, body: text, ts: Date.now(), state: "pending", forward, forwardOf: source.id };
    const ch = this.snap.byChannel[channelId] ?? emptyChannel();
    this.setChannel(channelId, { messages: [...ch.messages, optimistic] });
    this.transmit({ t: "send", id, channelId, body: text, forward: source.id });
    track("message_sent", { where: this.whereKind(channelId), attachments: 0, sticker: false, reply: false, forward: true });
  }

  retry(channelId: string, id: string) {
    const ch = this.snap.byChannel[channelId];
    const m = ch?.messages.find((x) => x.id === id);
    if (!ch || !m) return;
    this.setChannel(channelId, { messages: ch.messages.map((x) => (x.id === id ? { ...x, state: "pending", error: undefined } : x)) });
    if (m.forwardOf) return this.transmit({ t: "send", id, channelId, body: m.body, forward: m.forwardOf });
    this.transmit({ t: "send", id, channelId, body: m.body, replyTo: m.replyTo?.id, attachments: m.attachments });
  }

  /**
   * Takes a failed send off the screen (Delete beside Retry): one the room refused would fail again on every Retry
   * (#703). It is only on this device (failed sends leave the outbox and are never cached), so nothing goes to the room.
   */
  discard(channelId: string, id: string) {
    const ch = this.snap.byChannel[channelId];
    if (!ch?.messages.some((m) => m.id === id && m.state === "failed")) return;
    this.setChannel(channelId, { messages: ch.messages.filter((m) => m.id !== id) });
  }

  /**
   * Adds or removes my reaction, showing it immediately; the room's broadcast confirms it (idempotently). `superReact`
   * asks the room for an effect (#87); it plays when that broadcast comes back, as on everyone else's screen.
   * `jumbo` (#714) makes my reaction jumbo, also one I already have; it replaces `superReact`, and its thud plays the same way.
   */
  react(channelId: string, messageId: string, emoji: string, on: boolean, superReact = false, jumbo = false) {
    if (!this.snap.me) return;
    // A server emoji joins the reaction already there with the same id (it may have been renamed since), as the room does.
    const id = customEmojiId(emoji);
    if (id && on) {
      const m = this.snap.byChannel[channelId]?.messages.find((x) => x.id === messageId);
      emoji = Object.keys(m?.reactions ?? {}).find((k) => customEmojiId(k) === id) ?? emoji;
    }
    if (!this.sendable()) return this.offlineNotice();
    const big = on && jumbo;
    this.applyReaction(channelId, messageId, emoji, this.snap.me, on, big);
    this.raw({ t: "react", channelId, messageId, emoji, on, ...(big ? { jumbo: true } : on && superReact ? { effect: "random" as const } : {}) });
    if (on) track("reaction_added", { custom: !!id, where: this.whereKind(channelId) });
  }

  /** `jumbo` (#714): the reaction is now jumbo; a plain add leaves an existing jumbo reaction as it is. */
  private applyReaction(channelId: string, messageId: string, emoji: string, userId: string, on: boolean, jumbo = false) {
    const ch = this.snap.byChannel[channelId];
    if (!ch?.messages.some((m) => m.id === messageId)) return;
    const messages = ch.messages.map((m) => {
      if (m.id !== messageId) return m;
      const reactions = { ...m.reactions };
      const users = reactions[emoji] ?? [];
      if (on && !users.includes(userId)) reactions[emoji] = [...users, userId];
      // jumbo[emoji] stays a subset of reactions[emoji]: the same people, and gone when the last one goes.
      const big = { ...m.jumbo };
      if (on && jumbo && !(big[emoji] ?? []).includes(userId)) big[emoji] = [...(big[emoji] ?? []), userId];
      if (!on) {
        const left = users.filter((u) => u !== userId);
        if (left.length) reactions[emoji] = left;
        else delete reactions[emoji];
        const bigLeft = (big[emoji] ?? []).filter((u) => u !== userId);
        if (bigLeft.length) big[emoji] = bigLeft;
        else delete big[emoji];
      }
      const next = { ...m, reactions };
      if (Object.keys(big).length) next.jumbo = big;
      else delete next.jumbo;
      return next;
    });
    this.setChannel(channelId, { messages });
  }

  /** Changes my message's text, showing it at once; the room broadcasts it to everyone. False when offline (nothing sent, the notice says so). */
  edit(channelId: string, messageId: string, body: string): boolean {
    if (!this.sendable()) {
      this.offlineNotice();
      return false; // nothing was sent: the caller keeps the edit open
    }
    const ch = this.snap.byChannel[channelId];
    if (ch) this.setChannel(channelId, { messages: ch.messages.map((m) => (m.id === messageId ? { ...m, body: body.trim(), editedAt: Date.now() } : m)) });
    this.raw({ t: "edit", channelId, messageId, body });
    return true;
  }

  /**
   * Removes a message's link previews for everyone (the room checks it's mine, or that I'm a mod). The row changes when
   * the room answers everyone with an `embeds` event, not before: a refusal shows its words as a notice and leaves
   * the row as it was (#273, as the native apps do since #225).
   */
  suppressEmbeds(channelId: string, messageId: string) {
    this.mediaEdit({ t: "suppressEmbeds", channelId, messageId });
  }

  /**
   * Removes one attachment from a message for everyone (the room checks it's mine, or that I'm a mod). The row changes
   * on the room's `attachments` event; when nothing would be left the room deletes the message and says so.
   */
  removeAttachment(channelId: string, messageId: string, ref: string) {
    this.mediaEdit({ t: "removeAttachment", channelId, messageId, ref });
  }

  /** Marks a picture in my message private (blurred for everyone until they look) or not. The row changes on the room's `attachments` event. */
  setAttachmentPrivate(channelId: string, messageId: string, ref: string, on: boolean) {
    this.mediaEdit({ t: "setAttachmentPrivate", channelId, messageId, ref, private: on });
  }

  /**
   * Sends a media edit and leaves the row alone: the room's `embeds` or `attachments` event is the answer, and its
   * refusal has no `id` or `rid` to match, so there is nothing to roll back (#273). Offline, nothing is sent and the
   * notice says so.
   */
  private mediaEdit(e: ClientEvent) {
    if (this.raw(e)) return;
    this.offlineNotice();
  }

  /**
   * A reaction, edit or delete made offline is not sent and not shown, and the notice says so (#514). It used to show
   * on the screen and then vanish. The send of a message is different: it waits in the outbox.
   */
  private offlineNotice() {
    this.emit({ notice: { n: (this.snap.notice?.n ?? 0) + 1, text: "You're offline." } });
  }

  private sendable() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /** My newest message in a stream that can be edited (for ↑ in an empty composer). */
  lastOwnMessage(channelId: string): ChatMessage | undefined {
    const ch = this.snap.byChannel[channelId];
    return ch?.messages.findLast((m) => m.authorId === this.snap.me && !m.state && !m.deleted && !m.card);
  }

  /** Deletes a message for everyone (the room checks it's mine, or that I'm an owner or admin). */
  remove(channelId: string, messageId: string) {
    if (!this.sendable()) return this.offlineNotice();
    const ch = this.snap.byChannel[channelId];
    if (ch) this.setChannel(channelId, { messages: ch.messages.map((m) => (m.id === messageId ? { ...m, deleted: true, body: "", attachments: undefined } : m)) });
    this.raw({ t: "delete", channelId, messageId });
  }

  /** Starts showing a thread: fetches its newest page (and keeps it fresh until closeThread). */
  openThread(parentId: string) {
    const id = threadStreamId(parentId);
    // Drawn from the device at once if we kept it; the newest page from the room replaces it a moment later.
    const cached = this.threadCache.get(id);
    if (!this.snap.byChannel[id]) this.setChannel(id, { messages: cached?.messages ?? [], more: cached?.more ?? false, loadingOlder: true });
    this.raw({ t: "history", rid: String(++this.rid), channelId: id });
  }

  closeThread(parentId: string) {
    const { [threadStreamId(parentId)]: _, ...rest } = this.snap.byChannel;
    this.emit({ byChannel: rest });
  }

  private transmit(e: Extract<ClientEvent, { t: "send" }>) {
    this.outbox.push(e);
    this.sentAt.set(e.id, performance.now());
    if (this.snap.status === "live" && this.raw(e)) {
      // If no ack arrives in 15 s of a live socket, show it as failed so the person can retry.
      this.startSendClock(e.id);
    }
    // Else it is down (or not yet "ready"): it goes out on "ready" (the outbox), with no failure clock until then (#514).
  }

  private startSendClock(id: string) {
    this.stopSendClock(id);
    const ws = this.ws;
    const heard = this.heardCount;
    this.sendClocks.set(
      id,
      window.setTimeout(() => {
        this.sendClocks.delete(id);
        if (!this.outbox.some((s) => s.id === id)) return;
        // Nothing at all came from the socket since the send: it is dead, though it still looks live. Close it as the
        // keepalive does and leave the send pending: the reconnect sends it again (#514).
        if (ws && this.ws === ws && this.heardCount === heard) return this.kill(ws);
        // The socket answered and still no ack came: show the send as failed.
        this.fail(id, "Not sent yet");
      }, SEND_CLOCK_MS),
    );
    // The answer to this ping (or any other message) tells the clock the socket is alive.
    try {
      ws?.send("ping");
    } catch {
      // ignore: the clock ends in a kill
    }
  }

  private stopSendClock(id: string) {
    clearTimeout(this.sendClocks.get(id));
    this.sendClocks.delete(id);
  }

  private stopSendClocks() {
    for (const t of this.sendClocks.values()) clearTimeout(t);
    this.sendClocks.clear();
  }

  typing(channelId: string) {
    const last = this.lastTypingSent.get(channelId) ?? 0;
    if (Date.now() - last < TYPING_THROTTLE_MS) return;
    this.lastTypingSent.set(channelId, Date.now());
    this.raw({ t: "typing", channelId });
  }

  loadOlder(channelId: string) {
    const ch = this.snap.byChannel[channelId];
    const first = ch?.messages.find((m) => !m.state);
    if (!ch || !ch.more || ch.loadingOlder || !first) return;
    this.setChannel(channelId, { loadingOlder: true });
    this.raw({ t: "history", rid: String(++this.rid), channelId, beforeSeq: first.seq });
  }

  /**
   * Loads older messages until we hold `target`: the message after a read position (to show the NEW line), or a
   * message by id (a quote to jump to). Big ranges come HISTORY_MAX at a time, at most MAX_UNREAD_JUMP in all.
   * Resolves when done (or when it can't get there).
   */
  loadBack(channelId: string, target: { seq: number } | { id: string }): Promise<void> {
    const prev = this.loadingBack.get(channelId) ?? Promise.resolve();
    const run = prev.then(async () => {
      // To the NEW line: at most MAX_UNREAD_JUMP back (opening a channel shouldn't load thousands). To a message
      // someone asked for (search, a collection, a link): as far as it takes, up to MAX_MESSAGE_JUMP.
      const rounds = Math.ceil(("id" in target ? MAX_MESSAGE_JUMP : MAX_UNREAD_JUMP) / HISTORY_MAX);
      for (let round = 0; round < rounds; round++) {
        const ch = this.snap.byChannel[channelId];
        const first = ch?.messages.find((m) => !m.state);
        if (!ch || !first) return;
        if ("id" in target ? ch.messages.some((m) => m.id === target.id) : first.seq <= target.seq + 1) return;
        if (!ch.more) return;
        const rid = String(++this.rid);
        const reply = new Promise<ServerEvent | null>((resolve) => {
          this.pendingRequests.set(rid, resolve);
          setTimeout(() => resolve(null), 10_000);
        });
        // A few messages above the first unread too, so the NEW line doesn't sit at the very top.
        const range = "id" in target ? { toId: target.id } : { afterSeq: Math.max(0, target.seq - 5) };
        this.setChannel(channelId, { loadingOlder: true });
        if (!this.raw({ t: "history", rid, channelId, beforeSeq: first.seq, ...range })) {
          this.pendingRequests.delete(rid);
          this.setChannel(channelId, { loadingOlder: false });
          return;
        }
        const r = await reply;
        if (r?.t !== "history") {
          this.pendingRequests.delete(rid);
          if (this.snap.byChannel[channelId]) this.setChannel(channelId, { loadingOlder: false });
          return;
        }
      }
    });
    const tracked = run.finally(() => {
      if (this.loadingBack.get(channelId) === tracked) this.loadingBack.delete(channelId);
    });
    this.loadingBack.set(channelId, tracked);
    return tracked;
  }

  /**
   * Unread channels whose first unread message we don't hold yet (more than a page arrived while away): load back
   * to it now, quietly and a couple at a time, the channel on screen first, so opening them lands on the NEW line
   * at once. (Every channel's newest messages already came with the catch-up.) Channels never opened are skipped:
   * they open at the bottom.
   */
  private prefetch() {
    const { channels, byChannel, read } = this.snap;
    const wanted = channels
      .filter((c) => {
        const r = read[c.id] ?? 0;
        const first = byChannel[c.id]?.messages.find((m) => !m.state);
        return r > 0 && c.lastSeq > r && c.lastSeq - r <= MAX_UNREAD_JUMP && !!first && first.seq > r + 1 && byChannel[c.id]!.more;
      })
      .sort((x, y) => Number(y.id === this.focus) - Number(x.id === this.focus));
    const next = async (): Promise<void> => {
      const c = wanted.shift();
      if (!c || this.stopped || !this.snap.synced) return;
      await this.loadBack(c.id, { seq: this.snap.read[c.id] ?? 0 });
      return next();
    };
    for (let i = 0; i < PREFETCH_CONCURRENCY; i++) void next();
  }

  /** My notification level for a channel or DM (null: the server's); shown at once, confirmed (and synced to my
   *  devices) by the room. */
  setNotify(channelId: string, level: NotifyLevel | null) {
    this.emit({ notify: withKey(this.snap.notify, channelId, level) });
    this.raw({ t: "notifyPref", channelId, level });
  }

  /** Closes a DM in my sidebar until its next message (`seq`: where it ends now), or opens it again (null); shown at
   *  once, confirmed (and synced to my devices) by the room. */
  closeDm(channelId: string, seq: number | null) {
    this.emit({ dmClosed: withKey(this.snap.dmClosed, channelId, seq) });
    this.raw({ t: "dmClose", channelId, seq });
  }

  /** Mutes a channel or DM, or (no channelId) the whole server; `for: null` unmutes. The room sets the end time. */
  setMute(channelId: string | undefined, f: MuteFor | null) {
    const until = f === null ? null : muteUntil(f);
    if (channelId === undefined) this.emit({ serverNotify: { ...this.snap.serverNotify, mutedUntil: until ?? undefined } });
    else this.emit({ mutes: withKey(this.snap.mutes, channelId, until) });
    this.raw(channelId === undefined ? { t: "notifyMute", for: f } : { t: "notifyMute", channelId, for: f });
  }

  /** My level for the whole server and whether @everyone notifies me here. */
  setServerNotify(patch: { level?: NotifyLevel; suppressEveryone?: boolean }) {
    this.emit({ serverNotify: { ...this.snap.serverNotify, ...patch } });
    this.raw({ t: "serverNotify", ...patch });
  }

  createChannel(name: string, categoryId: string | null = null): Promise<{ ok: true; channel: Channel } | { ok: false; message: string }> {
    const rid = String(++this.rid);
    return new Promise((resolve) => {
      this.pendingRequests.set(rid, (e) => {
        if (e.t === "channel") resolve({ ok: true, channel: e.channel });
        else if (e.t === "error") resolve({ ok: false, message: e.message });
      });
      if (!this.raw({ t: "createChannel", rid, name, categoryId })) resolve({ ok: false, message: "You're offline." });
    });
  }

  renameChannel(channelId: string, name: string): Promise<{ ok: true; channel: Channel } | { ok: false; message: string }> {
    const rid = String(++this.rid);
    return new Promise((resolve) => {
      this.pendingRequests.set(rid, (e) => {
        if (e.t === "channel") resolve({ ok: true, channel: e.channel });
        else if (e.t === "error") resolve({ ok: false, message: e.message });
      });
      if (!this.raw({ t: "renameChannel", rid, channelId, name })) resolve({ ok: false, message: "You're offline." });
    });
  }

  /** Finds or starts the DM with these people (not counting me); the room answers with its channel. */
  openDm(userIds: string[]): Promise<{ ok: true; channel: Channel } | { ok: false; message: string }> {
    const rid = String(++this.rid);
    return new Promise((resolve) => {
      this.pendingRequests.set(rid, (e) => {
        if (e.t === "channel") resolve({ ok: true, channel: e.channel });
        else if (e.t === "error") resolve({ ok: false, message: e.message });
      });
      if (!this.raw({ t: "openDm", rid, userIds })) resolve({ ok: false, message: "You're offline." });
    });
  }

  // ---- Message links ----

  private refs = new Map<string, Promise<{ message: ChatMessage; rootId: string } | null | undefined>>();
  private liveWaiters: (() => void)[] = [];
  /**
   * The message a message link points at, with the channel or DM it's in; null if it's gone or not mine to see;
   * undefined if the room didn't answer (it waits for the connection first). One lookup per message this session;
   * an answer that didn't come isn't kept, so it's asked again next time.
   */
  messageRef(messageId: string): Promise<{ message: ChatMessage; rootId: string } | null | undefined> {
    const known = this.refs.get(messageId);
    if (known) return known;
    const live = this.snap.status === "live" ? Promise.resolve() : new Promise<void>((r) => this.liveWaiters.push(r));
    const p = live.then(() => this.ask("messageRef", { t: "messageRef", rid: String(++this.rid), messageId })).then((r) => {
      if (r.t !== "messageRef") {
        this.refs.delete(messageId);
        return undefined;
      }
      return r.message && r.rootId ? { message: r.message as ChatMessage, rootId: r.rootId } : null;
    });
    this.refs.set(messageId, p);
    return p;
  }

  // ---- Server settings (owners and admins; the room checks) ----

  /** Sends a settings request and resolves with the room's answer: the event named `expect`, or its error. */
  private request<K extends ServerEvent["t"]>(expect: K, event: ClientEvent & { rid: string }): Promise<Answer<K>> {
    return new Promise((resolve) => {
      this.pendingRequests.set(event.rid, (e) => {
        if (e.t === expect) resolve({ ok: true, event: e as Extract<ServerEvent, { t: K }> });
        else if (e.t === "error") resolve({ ok: false, message: e.message });
      });
      if (!this.raw(event)) {
        this.pendingRequests.delete(event.rid);
        resolve({ ok: false, message: "You're offline." });
      }
    });
  }
  setTopic(channelId: string, topic: string) {
    return this.request("channel", { t: "setTopic", rid: String(++this.rid), channelId, topic });
  }
  /** Owners and admins: allow or stop one app's cards in a text channel. */
  setAppCards(channelId: string, app: string, on: boolean) {
    return this.request("channel", { t: "setAppCards", rid: String(++this.rid), channelId, app, on });
  }
  deleteChannel(channelId: string) {
    return this.request("channelDeleted", { t: "deleteChannel", rid: String(++this.rid), channelId });
  }
  /** Puts a channel at `position` among `categoryId`'s channels (null: uncategorised). */
  moveChannel(channelId: string, categoryId: string | null, position: number) {
    return this.request("structure", { t: "moveChannel", rid: String(++this.rid), channelId, categoryId, position });
  }
  renameCategory(categoryId: string, name: string) {
    return this.request("category", { t: "renameCategory", rid: String(++this.rid), categoryId, name });
  }
  moveCategory(categoryId: string, position: number) {
    return this.request("structure", { t: "moveCategory", rid: String(++this.rid), categoryId, position });
  }
  deleteCategory(categoryId: string) {
    return this.request("categoryDeleted", { t: "deleteCategory", rid: String(++this.rid), categoryId });
  }

  // ---- Apps (docs/apps.md) ----

  /** Sends a request and resolves with the room's answer of kind `expect` (or null on error/offline/timeout). */
  private ask<K extends ServerEvent["t"]>(expect: K, event: ClientEvent & { rid: string }, timeoutMs = 10_000): Promise<Extract<ServerEvent, { t: K }> | { t: "error"; message: string }> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.pendingRequests.delete(event.rid);
        resolve({ t: "error", message: "Bloom didn't answer. Try again?" });
      }, timeoutMs);
      this.pendingRequests.set(event.rid, (e) => {
        clearTimeout(timer);
        if (e.t === expect) resolve(e as Extract<ServerEvent, { t: K }>);
        else if (e.t === "error") resolve({ t: "error", message: e.message });
      });
      if (!this.raw(event)) {
        clearTimeout(timer);
        this.pendingRequests.delete(event.rid);
        resolve({ t: "error", message: "You're offline." });
      }
    });
  }

  /** Runs a slash command in a stream. A form comes back as a private card; a posted card as a message. */
  async command(channelId: string, app: string, command: string, args: Record<string, unknown>): Promise<{ ok: boolean; message?: string }> {
    const r = await this.ask("commandResult", { t: "command", rid: String(++this.rid), channelId, app, command, args });
    // Built-in apps by name; apps people bloomed only as "bloomed" (their names are the group's own words).
    if (r.t === "commandResult" && r.ok) track("app_command_run", { app: app === "fr" ? "fr" : "bloomed" });
    return r.t === "commandResult" ? { ok: r.ok, message: r.message } : { ok: false, message: r.message };
  }

  /** Calls an app's action (from a card, a form, or a full view). */
  async callAction(app: string, action: string, input: Record<string, unknown>, opts: { channelId?: string; cardId?: string } = {}): Promise<AppResult> {
    const r = await this.ask("appResult", { t: "appCall", rid: String(++this.rid), app, action, input, ...opts });
    return toResult(r);
  }

  async restore(app: string, eventId: number): Promise<AppResult> {
    return toResult(await this.ask("appResult", { t: "appRestore", rid: String(++this.rid), app, eventId }));
  }

  /** Outside data for an app's full view (docs/integrations.md), through the room. */
  async callIntegration(app: string, integration: string, endpoint: string, params: Record<string, unknown>): Promise<{ ok: true; data: unknown } | { ok: false; code: string; message: string }> {
    const r = await this.ask("integrationResult", { t: "integrationCall", rid: String(++this.rid), app, integration, endpoint, params }, 20_000);
    if (r.t !== "integrationResult") return { ok: false, code: "upstream", message: r.message };
    return r.ok ? { ok: true, data: r.data } : { ok: false, code: r.code, message: r.message };
  }

  /** A card a full view asks to post, in a channel I can see. */
  async postCard(app: string, channelId: string, card: Card): Promise<AppResult> {
    return toResult(await this.ask("appResult", { t: "appPostCard", rid: String(++this.rid), app, channelId, card }));
  }

  async appHistory(app: string, before?: number): Promise<{ events: AppEvent[]; more: boolean } | null> {
    const r = await this.ask("appHistory", { t: "appHistory", rid: String(++this.rid), app, ...(before ? { before } : {}) });
    return r.t === "appHistory" ? { events: r.events, more: r.more } : null;
  }

  async queryApp(app: string, collection: string): Promise<AppRecord[] | null> {
    const r = await this.ask("appRecords", { t: "appQuery", rid: String(++this.rid), app, collection });
    return r.t === "appRecords" ? r.records : null;
  }

  // ---- Blooming (docs/blooming.md) ----

  private async bloomAsk(event: ClientEvent & { rid: string }, timeoutMs = 15_000): Promise<{ ok: true; result?: unknown } | { ok: false; message: string }> {
    const r = await this.ask("bloomResult", event, timeoutMs);
    if (r.t !== "bloomResult") return { ok: false, message: r.message };
    return r.ok ? { ok: true, result: r.result } : { ok: false, message: r.message ?? "That didn't work." };
  }

  /** Starts a blooming thread in a channel ("Bloom a new app"), or (`bot`) a bot's in my DM with it ("Make a new bot"). */
  async bloomStart(channelId: string, prompt: string, opts: { bot?: boolean } = {}): Promise<{ ok: true; threadId: string } | { ok: false; message: string }> {
    const r = await this.bloomAsk({ t: "bloomStart", rid: String(++this.rid), channelId, prompt, ...(opts.bot ? { bot: true } : {}) });
    if (r.ok) track("blooming_started");
    return r.ok ? { ok: true, threadId: (r.result as { threadId: string }).threadId } : r;
  }

  async bloomVersions(threadId: string): Promise<AppVersion[] | null> {
    const r = await this.ask("bloomVersions", { t: "bloomVersions", rid: String(++this.rid), threadId });
    return r.t === "bloomVersions" ? r.versions : null;
  }

  async bloomPublish(threadId: string, version: number) {
    const r = await this.bloomAsk({ t: "bloomPublish", rid: String(++this.rid), threadId, version });
    if (r.ok) track("app_published", { version });
    return r;
  }

  /** Where a stream is, for usage events: a channel, a thread, or a DM (or a thread under one). Never which. */
  private whereKind(stream: string): "channel" | "thread" | "dm" {
    const root = isThreadStream(stream) ? this.rootOf(stream) : stream;
    if (this.snap.channels.find((c) => c.id === root)?.kind === "dm") return "dm";
    return isThreadStream(stream) ? "thread" : "channel";
  }

  bloomLock(threadId: string, locked: boolean) {
    return this.bloomAsk({ t: "bloomLock", rid: String(++this.rid), threadId, locked });
  }

  /** Shows another version in the thread's preview, and/or resets the preview's data. */
  bloomPreview(threadId: string, opts: { version?: number; reset?: boolean }) {
    return this.bloomAsk({ t: "bloomPreview", rid: String(++this.rid), threadId, ...opts });
  }

  /** Every command's chat cards for the thread's draft (and live version): dry runs on the preview's data, nothing written. */
  async bloomCards(threadId: string): Promise<{ ok: true; previews: CardPreviews } | { ok: false; message: string }> {
    const r = await this.bloomAsk({ t: "bloomCards", rid: String(++this.rid), threadId }, 45_000);
    return r.ok ? { ok: true, previews: r.result as CardPreviews } : r;
  }

  /** Owners and admins: moves a built-in app into a blooming thread in `channelId` ("Edit with Bloom"). */
  async bloomAdopt(app: string, channelId: string): Promise<{ ok: true; threadId: string } | { ok: false; message: string }> {
    const r = await this.bloomAsk({ t: "bloomAdopt", rid: String(++this.rid), app, channelId }, 60_000);
    return r.ok ? { ok: true, threadId: (r.result as { threadId: string }).threadId } : r;
  }

  /** A bot's settings (docs/bots.md): who may talk to it, on/off, or one channel it's in. */
  async botSettings(app: string, patch: { access?: BotAccess; disabled?: boolean; channelId?: string; on?: boolean }): Promise<{ ok: true; bot: BotInfo } | { ok: false; message: string }> {
    const r = await this.bloomAsk({ t: "botSettings", rid: String(++this.rid), app, ...patch });
    if (!r.ok) return r;
    const bot = r.result as BotInfo;
    this.emit({ bots: this.snap.bots.map((b) => (b.app === bot.app ? bot : b)) });
    return { ok: true, bot };
  }

  /** I opened an app's full view (a bot with an appOpen trigger answers in our DM). */
  appOpened(app: string) {
    if (this.snap.bots.some((b) => b.app === app && b.triggers.includes("appOpen"))) this.raw({ t: "appOpened", app });
  }

  setAppEditors(app: string, editors: string[]) {
    return this.bloomAsk({ t: "appEditors", rid: String(++this.rid), app, editors });
  }

  /** Delete a bloomed app (creator, admin, or server owner only). */
  deleteApp(app: string) {
    return this.bloomAsk({ t: "appDelete", rid: String(++this.rid), app });
  }

  /** Delete an unpublished draft version from a blooming thread (creator, admin, or server owner only). */
  deleteVersion(threadId: string, version: number) {
    return this.bloomAsk({ t: "bloomDeleteVersion", rid: String(++this.rid), threadId, version });
  }

  /** Throws away one of my private cards (on all my devices). Gone here at once. */
  dismissCard(id: string) {
    this.emit({ privateCards: this.snap.privateCards.filter((c) => c.id !== id) });
    this.raw({ t: "dismissCard", id });
  }

  /** Keeps an app's collection loaded and live while someone looks at it (cards, full views). */
  watchCollection = (app: string, collection: string) => {
    const k = `${app}/${collection}`;
    const n = this.watching.get(k) ?? 0;
    this.watching.set(k, n + 1);
    if (n === 0) this.startWatch(k);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      const left = (this.watching.get(k) ?? 1) - 1;
      if (left > 0) return void this.watching.set(k, left);
      this.watching.delete(k);
      // Not instantly: a card scrolling out and back in shouldn't resubscribe each time.
      setTimeout(() => {
        if (this.watching.has(k)) return;
        const [a, c] = k.split("/") as [string, string];
        this.raw({ t: "appSub", app: a, collection: c, on: false });
      }, 30_000);
    };
  };

  private startWatch(k: string) {
    const [app, collection] = k.split("/") as [string, string];
    if (this.raw({ t: "appSub", app, collection, on: true })) void this.queryApp(app, collection);
  }

  /** For cards and full views: the live store plus watching. */
  get appSource(): AppDataSource {
    return (this._appSource ??= { data: this.appData, watchCollection: this.watchCollection });
  }
  private _appSource: AppDataSource | undefined;

  createCategory(name: string): Promise<{ ok: true; category: Category } | { ok: false; message: string }> {
    const rid = String(++this.rid);
    return new Promise((resolve) => {
      this.pendingRequests.set(rid, (e) => {
        if (e.t === "category") resolve({ ok: true, category: e.category });
        else if (e.t === "error") resolve({ ok: false, message: e.message });
      });
      if (!this.raw({ t: "createCategory", rid, name })) resolve({ ok: false, message: "You're offline." });
    });
  }
}

/** Server-level events, for pages that react to them (see Room.onServerEvent). */
export type ServerLevelEvent = Extract<ServerEvent, { t: "server" | "role" | "memberLeft" }>;

/** The answer to a settings request: the event that confirms it, or the room's error message. */
export type Answer<K extends ServerEvent["t"]> = { ok: true; event: Extract<ServerEvent, { t: K }> } | { ok: false; message: string };

/** Input that counts as "I'm here". */
const ACTIVITY = ["pointerdown", "keydown", "pointermove", "wheel", "touchstart"] as const;

function toResult(r: Extract<ServerEvent, { t: "appResult" }> | { t: "error"; message: string }): AppResult {
  if (r.t !== "appResult") return { ok: false, message: r.message };
  return r.ok ? { ok: true, result: r.result } : { ok: false, message: r.message ?? "That didn't work.", ...(r.errors ? { errors: r.errors } : {}) };
}

/** A message's list ids as stored on it: none is undefined, so an empty array never sits on a message. */
function listsOrNone(ids: string[]): string[] | undefined {
  return ids.length ? ids : undefined;
}

/** Merges two seq-ordered lists, dropping duplicates by id. */
function mergeBySeq(a: ChatMessage[], b: ChatMessage[]): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  for (const m of a) byId.set(m.id, m);
  for (const m of b) byId.set(m.id, m);
  return [...byId.values()].sort((x, y) => x.seq - y.seq);
}

/** A copy of `rec` with `key` set to `value`, or without it when `value` is null. */
function withKey<T>(rec: Record<string, T>, key: string, value: T | null): Record<string, T> {
  const out = { ...rec };
  if (value === null) delete out[key];
  else out[key] = value;
  return out;
}
